import { useEffect, useRef } from 'react'
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { SortKey } from '../../types'

interface Column {
  /** SortKey field, or null for non-sortable columns. */
  field: SortKey['field'] | null
  label: string
  /** Right-align header text (numeric columns). */
  rightAlign?: boolean
  /** Hidden visually but kept in DOM for screen readers (icon-only columns). */
  visuallyHidden?: boolean
  /** Renders the select-all checkbox instead of a text/icon label. */
  isSelectAll?: boolean
}

const COLUMNS: readonly Column[] = [
  { field: null, label: 'Select all', isSelectAll: true },
  // Header label intentionally short ("Prio") — the column carries a
  // dot + 3-letter band (HIG/MED/LOW/CRT) so the full word is
  // redundant and was eating ~24px the Scenario column needed.
  { field: 'priority', label: 'Prio' },
  { field: 'alert_number', label: 'Alert' },
  { field: 'scenario_name', label: 'Scenario' },
  { field: 'score', label: 'Score', rightAlign: true },
  { field: null, label: 'Customer' },
  { field: 'amount_minor', label: 'Amount', rightAlign: true },
  { field: 'channel', label: 'Channel' },
  // Status stays icon-only — the column width (24px) is intentionally
  // sized for the icon glyph; rendering "STATUS" would overflow.
  { field: 'status', label: 'Status', visuallyHidden: true },
  { field: 'sla_remaining', label: 'SLA' },
  { field: null, label: 'Assigned' },
  { field: 'created_at', label: 'Created' },
] as const

interface Props {
  sort: SortKey[]
  onSort: (field: SortKey['field'], multi: boolean) => void
  /** Number of currently-loaded rows that are selected. */
  selectedCount: number
  /** Number of currently-loaded rows (select-all only covers what's
   *  loaded in the virtualized list, same scope as the `x` shortcut). */
  loadedCount: number
  onToggleSelectAll: () => void
}

export function AlertsTableHeader({
  sort,
  onSort,
  selectedCount,
  loadedCount,
  onToggleSelectAll,
}: Props) {
  return (
    <div
      role="row"
      className="sticky top-0 z-10 grid h-9 items-center gap-3 border-b border-border bg-card/95 px-3 text-xs font-medium uppercase tracking-wide text-muted-fg backdrop-blur grid-cols-[40px_48px_96px_minmax(20rem,1.4fr)_56px_minmax(10rem,1fr)_128px_56px_24px_112px_80px_80px]"
    >
      {COLUMNS.map((col, idx) => {
        const sortIndex = col.field ? sort.findIndex((s) => s.field === col.field) : -1
        const dir = sortIndex >= 0 ? sort[sortIndex]!.dir : null
        const sortable = col.field !== null
        const content = col.isSelectAll ? (
          <SelectAllCheckbox
            selectedCount={selectedCount}
            loadedCount={loadedCount}
            onToggleSelectAll={onToggleSelectAll}
          />
        ) : sortable ? (
          <button
            type="button"
            onClick={(e) => onSort(col.field!, e.shiftKey)}
            className={cn(
              'flex items-center gap-1 hover:text-fg focus-visible:outline-none focus-visible:text-fg',
              col.rightAlign && 'ml-auto',
            )}
          >
            <span className={cn(col.visuallyHidden && 'sr-only')}>{col.label}</span>
            {dir === 'asc' && <ArrowUp size={10} />}
            {dir === 'desc' && <ArrowDown size={10} />}
            {dir === null && !col.visuallyHidden && (
              <ChevronsUpDown size={10} className="opacity-30" />
            )}
            {sortIndex >= 0 && sort.length > 1 && (
              <span className="text-[0.5rem] font-mono">{sortIndex + 1}</span>
            )}
          </button>
        ) : (
          <span
            className={cn(col.visuallyHidden && 'sr-only', col.rightAlign && 'ml-auto')}
          >
            {col.label}
          </span>
        )
        return (
          // role="columnheader" — every direct child of role="row"
          // inside a grid must be a cell / rowheader / columnheader,
          // per WAI-ARIA. axe flags bare divs as the
          // aria-required-children violation.
          <div
            key={`${col.label}-${idx}`}
            role="columnheader"
            className={cn(col.isSelectAll && 'flex items-center justify-center')}
          >
            {content}
          </div>
        )
      })}
    </div>
  )
}

interface SelectAllCheckboxProps {
  selectedCount: number
  loadedCount: number
  onToggleSelectAll: () => void
}

/**
 * Select-all control for the queue header. Previously this column
 * rendered only an `sr-only` text label with no actual checkbox — the
 * `x` keyboard shortcut was the ONLY way to select-all, for sighted and
 * screen-reader users alike. This renders a real, visible, labeled
 * checkbox so both audiences get a discoverable affordance while
 * keeping the `x` shortcut working unchanged.
 */
function SelectAllCheckbox({
  selectedCount,
  loadedCount,
  onToggleSelectAll,
}: SelectAllCheckboxProps) {
  const ref = useRef<HTMLInputElement | null>(null)
  const allSelected = loadedCount > 0 && selectedCount === loadedCount
  const someSelected = selectedCount > 0 && !allSelected

  // React has no `indeterminate` prop for checkboxes — it's a DOM-only
  // visual state, set imperatively.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = someSelected
  }, [someSelected])

  const label = allSelected
    ? `Deselect all ${loadedCount} loaded alerts`
    : someSelected
      ? `Select all ${loadedCount} loaded alerts (${selectedCount} currently selected)`
      : `Select all ${loadedCount} loaded alerts`

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={allSelected}
      disabled={loadedCount === 0}
      onChange={onToggleSelectAll}
      aria-label={label}
      className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
    />
  )
}
